import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { CornerDownRight } from 'lucide-react';
import {
  groupItemsByProductName,
  lengthLabel,
  type ProductLengthGridItem,
} from '~/components/common/productLengthGrouping';
import { BundleQuantityLabel } from '~/components/ui/BundleQuantityLabel';
import { ProductLengthGridHeader } from './ProductLengthGridHeader';

const PRODUCT_LENGTH_GRID_CLASS = 'grid w-fit items-baseline gap-x-3 gap-y-1';

function gridTemplateColumns(
  hasPrice: boolean,
  hasActions: boolean,
  hasCarts: boolean
): string {
  return [
    'minmax(0,14rem)',
    'minmax(0,6rem)',
    'minmax(0,6rem)',
    hasPrice ? 'minmax(0,8rem)' : null,
    hasActions ? 'minmax(0,11rem)' : 'minmax(0,7rem)',
    hasCarts ? 'minmax(0,16rem)' : null,
  ]
    .filter(Boolean)
    .join(' ');
}

type ProductLengthGridColumn =
  | 'product'
  | 'productCode'
  | 'length'
  | 'price'
  | 'quantity'
  | 'piecesPerBundle'
  | 'carts';

type ProductLengthGridProps<T extends ProductLengthGridItem> = {
  items: T[];
  testId: string;
  id?: string;
  renderPrice?: (item: T) => ReactNode;
  priceLabel?: string;
  renderActions?: (item: T, rowIndex: number) => ReactNode;
  renderCarts?: (item: T) => ReactNode;
};

export function ProductLengthGrid<T extends ProductLengthGridItem>({
  items,
  testId,
  id,
  renderPrice,
  priceLabel,
  renderActions,
  renderCarts,
}: Readonly<ProductLengthGridProps<T>>) {
  const { t } = useTranslation('inventory');

  let nextRowIndex = 0;
  const groups = groupItemsByProductName(items).map((group, groupIndex) => ({
    group,
    rows: group.items.map((item, itemIndex) => ({
      item,
      rowIndex: nextRowIndex++,
      startsGroup: itemIndex === 0,
      spacing: itemIndex === 0 && groupIndex > 0 ? 'mt-2' : '',
    })),
  }));

  return (
    <div id={id} className="flex items-start gap-2" data-testid={testId}>
      <CornerDownRight
        size={14}
        aria-hidden="true"
        className="mt-0.5 shrink-0 text-neut-400"
      />
      <div
        role="table"
        aria-label={t('inventory.buy_list.products')}
        className={PRODUCT_LENGTH_GRID_CLASS}
        style={{
          gridTemplateColumns: gridTemplateColumns(
            Boolean(renderPrice),
            Boolean(renderActions),
            Boolean(renderCarts)
          ),
        }}
        data-testid={`${testId}-grid`}
      >
        <ProductLengthGridHeader
          hasPrice={Boolean(renderPrice)}
          hasCarts={Boolean(renderCarts)}
          priceLabel={priceLabel}
        />
        {groups.map(({ group, rows }) => (
          <div
            key={group.name}
            role="rowgroup"
            className="contents"
            data-testid={`${testId}-group-${group.name}`}
          >
            {rows.map(({ item, rowIndex, startsGroup, spacing }) => {
              const cellTestId = (column: ProductLengthGridColumn) =>
                `${testId}-${column}-${rowIndex}`;

              return (
                <div
                  key={item.id}
                  role="row"
                  className="contents"
                  data-testid={`${testId}-row-${item.id}`}
                >
                  <span
                    role="cell"
                    className={`pr-4 text-sm font-semibold text-neut-prim ${spacing}`}
                    data-testid={cellTestId('product')}
                  >
                    {startsGroup ? group.name : ''}
                  </span>
                  <span
                    role="cell"
                    className={`text-xs text-neut-sec ${spacing}`}
                    data-testid={cellTestId('productCode')}
                  >
                    {item.platformProductsLength.platformProduct.code}
                  </span>
                  <span
                    role="cell"
                    className={`text-xs text-neut-sec ${spacing}`}
                    data-testid={cellTestId('length')}
                  >
                    {lengthLabel(item)}
                  </span>
                  {renderPrice ? (
                    <span
                      role="cell"
                      className={`text-xs text-neut-prim ${spacing}`}
                      data-testid={cellTestId('price')}
                    >
                      {renderPrice(item)}
                    </span>
                  ) : null}
                  <span
                    role="cell"
                    className={`text-xs text-neut-prim ${spacing}`}
                    data-testid={cellTestId('quantity')}
                  >
                    <BundleQuantityLabel
                      quantity={item.quantity}
                      piecesPerBundle={item.effectivePiecesPerBundle}
                      platformProductsLength={item.platformProductsLength}
                      actions={
                        renderActions ? renderActions(item, rowIndex) : null
                      }
                    />
                    {item.effectivePiecesPerBundle > 0 ? (
                      <span
                        className="block text-right text-xs text-neut-sec"
                        data-testid={cellTestId('piecesPerBundle')}
                      >
                        {t('inventory.buy_list.pieces_per_bundle_value', {
                          count: item.effectivePiecesPerBundle,
                        })}
                      </span>
                    ) : null}
                  </span>
                  {renderCarts ? (
                    <span
                      role="cell"
                      className={`text-xs ${spacing}`}
                      data-testid={cellTestId('carts')}
                    >
                      {renderCarts(item)}
                    </span>
                  ) : null}
                </div>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
}
